import React, { useState, useRef, useEffect } from 'react';
import { Form, Button, Image, Spinner, Alert } from 'react-bootstrap';
import { FaUpload, FaTimes } from 'react-icons/fa';
import { uploadAPI } from '../services/api';

const ImageUpload = ({ currentImage, onImageChange, label = 'Image' }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(currentImage || '');
  const fileInputRef = useRef(null);

  // Update preview when currentImage prop changes
  useEffect(() => {
    setPreview(currentImage || '');
  }, [currentImage]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    setError('');
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      console.log('Uploading image:', file.name, 'Size:', file.size);
      const response = await uploadAPI.uploadImage(formData);
      console.log('Upload response:', response.data);
      
      // Construct full image URL
      const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
      const imageUrl = `${baseUrl}${response.data.imageUrl}`;
      
      console.log('Final image URL:', imageUrl);
      setPreview(imageUrl);
      onImageChange(imageUrl);
    } catch (error) {
      console.error('Upload error:', error);
      console.error('Error details:', error.response);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to upload image';
      setError(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setPreview('');
    onImageChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <Form.Label>{label}</Form.Label>
      
      {error && <Alert variant="danger" className="mb-2">{error}</Alert>}
      
      {preview ? (
        <div className="position-relative d-inline-block">
          <Image 
            src={preview} 
            alt="Preview" 
            thumbnail 
            style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
          />
          <Button
            variant="danger"
            size="sm"
            className="position-absolute top-0 end-0 m-1"
            onClick={handleRemoveImage}
            disabled={uploading}
          >
            <FaTimes />
          </Button>
        </div>
      ) : (
        <div className="border rounded p-4 text-center bg-light">
          <FaUpload size={40} className="text-muted mb-2" />
          <p className="text-muted mb-2">Click to upload image</p>
          <Form.Control
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            disabled={uploading}
          />
        </div>
      )}

      {uploading && (
        <div className="mt-2 text-center">
          <Spinner animation="border" size="sm" className="me-2" />
          <small>Uploading...</small>
        </div>
      )}

      <Form.Text className="text-muted d-block mt-2">
        Supported formats: JPEG, PNG, GIF, WebP (Max 5MB)
      </Form.Text>
    </div>
  );
};

export default ImageUpload;
